// src/pages/Finance.js
import React, { useEffect, useState, useRef } from "react";
import {
  db,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
} from "../firebase";
import { v4 as uuidv4 } from "uuid";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import CollapsibleSection from "../components/CollapsibleSection";
import currenciesData from "../data/currencies.json";

// Error Boundary to catch rendering errors
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return <div>Error: {this.state.error.message}</div>;
    }
    return this.props.children;
  }
}

// FeedbackModal component for consistent error/success messages
const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>
          {message}
        </p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// RateFetchModal for retrying exchange rate fetches
const RateFetchModal = ({ isFetching, retryCount, onRetry, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(5);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isFetching) {
      setTimeLeft(5);
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isFetching]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>Fetching Exchange Rates</h3>
        {isFetching ? (
          <>
            <p>Loading exchange rates... ({timeLeft}s)</p>
            <div className="loading-animation">
              <div className="spinner"></div>
            </div>
          </>
        ) : (
          <>
            <p>Failed to fetch exchange rates. Retry count: {retryCount}</p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={onRetry}>
                Retry Now
              </button>
              <button className="btn-secondary" onClick={onClose}>
                Try Again Later
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function Finance() {
  const navigate = useNavigate();
  const [clearances, setClearances] = useState([]);
  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [disposals, setDisposals] = useState([]);
  const [tripFinances, setTripFinances] = useState([]);
  const [financeForm, setFinanceForm] = useState({
    tripCode: "",
    income: "",
    currency: "",
    extraCosts: [],
  });
  const [defaultCurrency, setDefaultCurrency] = useState(localStorage.getItem("defaultCurrency") || "USD");
  const [rates, setRates] = useState({});
  const [summary, setSummary] = useState([]);
  const [filterTruck, setFilterTruck] = useState("All");
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [expandedFinanceId, setExpandedFinanceId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    financialSummary: true,
    costTemplates: true,
    tripFinancials: true,
    profitLossSummary: true,
    disposalHistory: true,
  });

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setFeedback(null);
      try {
        const [clearancesData, tripsData, routesData, trucksData, disposalsData, financesData, ratesData] = await Promise.all([
          loadClearances().catch((e) => {
            console.error("Failed to load clearances:", e);
            return [];
          }),
          loadTrips().catch((e) => {
            console.error("Failed to load trips:", e);
            return [];
          }),
          loadRoutes().catch((e) => {
            console.error("Failed to load routes:", e);
            return [];
          }),
          loadTrucks().catch((e) => {
            console.error("Failed to load trucks:", e);
            return [];
          }),
          loadDisposals().catch((e) => {
            console.error("Failed to load disposals:", e);
            return [];
          }),
          loadFinances().catch((e) => {
            console.error("Failed to load finances:", e);
            return [];
          }),
          fetchRatesWithRetry().catch((e) => {
            console.error("Failed to load rates:", e);
            return {};
          }),
        ]);

        setClearances(clearancesData);
        setTrips(tripsData);
        setRoutes(routesData);
        setTrucks(trucksData);
        setDisposals(disposalsData);
        setTripFinances(financesData);
        setRates(ratesData);
        aggregateSummary(financesData);
      } catch (error) {
        console.error("Error in loadAll:", error);
        setFeedback({
          message: "Failed to load finance data. Please try again.",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
    loadAll();

    const interval = setInterval(() => {
      fetchRatesWithRetry(true);
    }, 3600000);
    return () => clearInterval(interval);
  }, [defaultCurrency]);

  async function loadClearances() {
    const snaps = await getDocs(collection(db, "clearances"));
    return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function loadTrips() {
    const snaps = await getDocs(collection(db, "trips"));
    return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function loadRoutes() {
    const snaps = await getDocs(collection(db, "routes"));
    return snaps.docs.map((r) => ({ id: r.id, ...r.data() }));
  }

  async function loadTrucks() {
    const snaps = await getDocs(collection(db, "trucks"));
    return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function loadDisposals() {
    const snaps = await getDocs(collection(db, "disposals"));
    return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function loadFinances() {
    const snaps = await getDocs(collection(db, "trip_finances"));
    return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function fetchRatesWithRetry(isBackground = false) {
    if (!isBackground) setIsFetchingRates(true);
    setRetryCount((prev) => prev + 1);
    try {
      const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${defaultCurrency}`);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const data = await res.json();
      setRates(data.rates || {});
      setIsFetchingRates(false);
      setRetryCount(0);
      localStorage.setItem(`rates_${defaultCurrency}`, JSON.stringify({ rates: data.rates, timestamp: Date.now() }));
      return data.rates || {};
    } catch (e) {
      console.error("Failed to fetch rates:", e);
      const cached = localStorage.getItem(`rates_${defaultCurrency}`);
      if (cached) {
        const { rates, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 3600000) {
          setRates(rates);
          setIsFetchingRates(false);
          setRetryCount(0);
          return rates;
        }
      }
      setIsFetchingRates(false);
      return {};
    }
  }

  async function saveFinance(e) {
    e.preventDefault();
    if (!financeForm.tripCode || !financeForm.income || !financeForm.currency) {
      setFeedback({ message: "Trip code, income, and currency are required.", type: "error" });
      return;
    }
    if (isNaN(parseFloat(financeForm.income))) {
      setFeedback({ message: "Income must be a valid number.", type: "error" });
      return;
    }
    try {
      const incomeNum = parseFloat(financeForm.income || 0);
      const rateIncome = rates[financeForm.currency] || 1;
      const rateDefault = rates[defaultCurrency] || 1;
      const incomeDefault = (incomeNum / rateIncome) * rateDefault;
      let baseCosts = 0;
      (financeForm.extraCosts || []).forEach((c) => {
        if (!c.currency) throw new Error(`Currency required for extra cost: ${c.desc}`);
        if (isNaN(parseFloat(c.amount))) {
          throw new Error(`Invalid amount for extra cost: ${c.desc}`);
        }
        const amt = parseFloat(c.amount || 0);
        const rate = rates[c.currency] || 1;
        baseCosts += (amt / rate) * rateDefault;
      });
      const profit = incomeDefault - baseCosts;
      const selectedTrip = trips.find(
        (t) => t.trip_code === financeForm.tripCode
      );
      const payload = {
        ...financeForm,
        incomeDefault,
        totalCostDefault: baseCosts,
        profit,
        truck_id: selectedTrip?.truck || "",
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, "trip_finances"), payload);
      if (selectedTrip) {
        const tripRef = doc(db, "trips", selectedTrip.id);
        const prev = parseFloat(selectedTrip.total_cost || 0);
        const updatedCost = prev + baseCosts;
        await updateDoc(tripRef, { total_cost: updatedCost });
        await loadTrips();
      }
      setFinanceForm({
        tripCode: "",
        income: "",
        currency: defaultCurrency,
        extraCosts: [],
      });
      setFeedback({ message: "Financial record saved successfully.", type: "success" });
      await loadFinances();
    } catch (error) {
      console.error("Error saving finance:", error);
      setFeedback({
        message: "Failed to save finance record: " + error.message,
        type: "error",
      });
    }
  }

  async function deleteFinance(id) {
    if (!window.confirm("Delete finance record?")) return;
    try {
      await deleteDoc(doc(db, "trip_finances", id));
      setFeedback({ message: "Finance record deleted successfully.", type: "success" });
      await loadFinances();
    } catch (error) {
      console.error("Error deleting finance record:", error);
      setFeedback({
        message: "Failed to delete finance record: " + error.message,
        type: "error",
      });
    }
  }

  function addExtraCostRow() {
    setFinanceForm({
      ...financeForm,
      extraCosts: [
        ...(financeForm.extraCosts || []),
        { desc: "", amount: "", currency: defaultCurrency },
      ],
    });
  }

  function handleExtraChange(i, field, value) {
    const arr = [...(financeForm.extraCosts || [])];
    arr[i] = { ...arr[i], [field]: value };
    if (field === "amount" && arr[i].currency) {
      const amount = parseFloat(value) || 0;
      arr[i].amountDefault = (amount / (rates[arr[i].currency] || 1)) * (rates[defaultCurrency] || 1);
    }
    setFinanceForm({ ...financeForm, extraCosts: arr });
  }

  function aggregateSummary(data) {
    const map = {};
    data.forEach((d) => {
      const ts = d.createdAt?.seconds ? d.createdAt.seconds * 1000 : Date.now();
      const month = new Date(ts).toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      if (!map[month]) map[month] = { month, income: 0, cost: 0, profit: 0 };
      map[month].income += d.incomeDefault || 0;
      map[month].cost += d.totalCostDefault || 0;
      map[month].profit += d.profit || 0;
    });
    disposals.forEach((d) => {
      const ts = d.date?.seconds ? d.date.seconds * 1000 : Date.now();
      const month = new Date(ts).toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      if (!map[month]) map[month] = { month, income: 0, cost: 0, profit: 0 };
      const truck = trucks.find((t) => t.id === d.truck_id);
      const bookValue = truck?.book_value || 0;
      if (d.type === "write-off") {
        map[month].cost += d.cost || 0;
        map[month].profit -= d.cost || 0;
      } else {
        const gainLoss = (d.amount || 0) - bookValue;
        map[month].income += gainLoss > 0 ? gainLoss : 0;
        map[month].cost += gainLoss < 0 ? -gainLoss : 0;
        map[month].profit += gainLoss;
      }
    });
    setSummary(Object.values(map));
  }

  const trucksList = [...new Set(trips.map((t) => t.truck).filter(Boolean))];
  const filteredFinances =
    filterTruck === "All"
      ? tripFinances
      : tripFinances.filter((f) => f.truck_id === filterTruck);

  function exportCSV() {
    const headers = ["Trip Code", "Truck ID", "Distance (km)", `Income (${defaultCurrency})`, `Cost (${defaultCurrency})`, `Profit (${defaultCurrency})`];
    const rows = filteredFinances.map((f) => {
      const trip = trips.find((t) => t.trip_code === f.tripCode);
      const route = trip ? routes.find((r) => r.id === trip.route_id) : null;
      return [
        f.tripCode || "",
        f.truck_id || "",
        route ? (route.total_distance_km || "-") : "-",
        (f.incomeDefault || 0).toFixed(2),
        (f.totalCostDefault || 0).toFixed(2),
        (f.profit || 0).toFixed(2),
      ];
    });
    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "ProfitLoss_Report.csv");
  }

  function exportPDF() {
    const docPDF = new jsPDF();
    docPDF.text("Profit & Loss Summary", 14, 15);
    const tableData = filteredFinances.map((f) => {
      const trip = trips.find((t) => t.trip_code === f.tripCode);
      const route = trip ? routes.find((r) => r.id === trip.route_id) : null;
      return [
        f.tripCode || "",
        f.truck_id || "",
        route ? (route.total_distance_km || "-") : "-",
        (f.incomeDefault || 0).toFixed(2),
        (f.totalCostDefault || 0).toFixed(2),
        (f.profit || 0).toFixed(2),
      ];
    });
    docPDF.autoTable({
      head: [["Trip Code", "Truck ID", "Distance (km)", `Income (${defaultCurrency})`, `Cost (${defaultCurrency})`, `Profit (${defaultCurrency})`]],
      body: tableData,
    });
    docPDF.save("ProfitLoss_Report.pdf");
  }

  function goToCostTemplates() {
    navigate("/cost-templates", { state: { defaultCurrency, rates } });
  }

  const vehicleAssets = trucks
    .filter((t) => !t.disposed)
    .reduce((sum, t) => sum + (t.book_value || 0), 0);

  const totalIncome = filteredFinances.reduce(
    (s, f) => s + (f.incomeDefault || 0),
    0
  ) + disposals.reduce((s, d) => {
    const truck = trucks.find((t) => t.id === d.truck_id);
    const bookValue = truck?.book_value || 0;
    return d.type === "sold" && (d.amount - bookValue) > 0 ? s + (d.amount - bookValue) : s;
  }, 0);

  const totalCost = filteredFinances.reduce(
    (s, f) => s + (f.totalCostDefault || 0),
    0
  ) + disposals.reduce((s, d) => {
    const truck = trucks.find((t) => t.id === d.truck_id);
    const bookValue = truck?.book_value || 0;
    return d.type === "write-off" ? s + (d.cost || 0) : (d.type === "sold" && (bookValue - d.amount) > 0 ? s + (bookValue - d.amount) : s);
  }, 0);

  const totalProfit = totalIncome - totalCost;

  const handleSectionToggle = (sectionId) => {
    setExpandedSections({
      ...expandedSections,
      [sectionId]: !expandedSections[sectionId],
    });
  };

  const handleTripChange = (tripCode) => {
    setFinanceForm({
      ...financeForm,
      tripCode,
    });
  };

  const handleDefaultCurrencyChange = async (currency) => {
    setDefaultCurrency(currency);
    localStorage.setItem("defaultCurrency", currency);
    setLoading(true);
    try {
      const ratesData = await fetchRatesWithRetry();
      const updatedFinances = tripFinances.map((f) => {
        const incomeDefault = (parseFloat(f.income) / (rates[f.currency] || 1)) * (ratesData[currency] || 1);
        const totalCostDefault = f.extraCosts.reduce(
          (sum, c) => sum + ((parseFloat(c.amount) / (rates[c.currency] || 1)) * (ratesData[currency] || 1)),
          0
        );
        return { ...f, incomeDefault, totalCostDefault, profit: incomeDefault - totalCostDefault };
      });
      setTripFinances(updatedFinances);
      aggregateSummary(updatedFinances);
      const updatedClearances = clearances.map((c) => {
        const totalCostDefault = c.entries.reduce(
          (sum, e) => sum + ((e.cost / (rates[e.currency] || 1)) * (ratesData[currency] || 1)),
          0
        );
        return { ...c, totalCostDefault };
      });
      setClearances(updatedClearances);
      setFeedback({ message: "Currency updated successfully.", type: "success" });
    } catch (e) {
      console.error("Failed to update rates:", e);
      setFeedback({ message: "Failed to update exchange rates.", type: "error" });
    }
    setLoading(false);
  };

  return (
    <ErrorBoundary>
      <div className="finance-page">
        {isFetchingRates || retryCount > 0 ? (
          <RateFetchModal
            isFetching={isFetchingRates}
            retryCount={retryCount}
            onRetry={() => fetchRatesWithRetry()}
            onClose={() => setRetryCount(0)}
          />
        ) : feedback ? (
          <FeedbackModal
            message={feedback.message}
            type={feedback.type}
            onClose={() => setFeedback(null)}
          />
        ) : null}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <h2>Finance</h2>
            
            {/* Financial Summary Section */}
            <CollapsibleSection
              title="Financial Summary"
              id="financialSummary"
              isExpanded={expandedSections.financialSummary}
              onToggle={handleSectionToggle}
              externalControl={true}
            >
              <div className="summary-cards">
                <div className="card">
                  <div className="card-title">Vehicle Assets ({defaultCurrency})</div>
                  <div className="card-value">{vehicleAssets.toFixed(2)}</div>
                </div>
                <div className="card">
                  <div className="card-title">Total Income ({defaultCurrency})</div>
                  <div className="card-value">{totalIncome.toFixed(2)}</div>
                </div>
                <div className="card">
                  <div className="card-title">Total Cost ({defaultCurrency})</div>
                  <div className="card-value">{totalCost.toFixed(2)}</div>
                </div>
                <div className="card">
                  <div className="card-title">Net Profit ({defaultCurrency})</div>
                  <div className="card-value">{totalProfit.toFixed(2)}</div>
                </div>
                <div className="card currency-card">
                  <div className="card-title">Default Currency</div>
                  <select
                    className="currency-dropdown"
                    value={defaultCurrency}
                    onChange={(e) => handleDefaultCurrencyChange(e.target.value)}
                  >
                    <option value="">Select Default Currency</option>
                    {currenciesData.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.symbol})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CollapsibleSection>

            {/* Cost Templates Section - Simplified (Management only) */}
            <CollapsibleSection
              title="Cost Templates"
              id="costTemplates"
              isExpanded={expandedSections.costTemplates}
              onToggle={handleSectionToggle}
              externalControl={true}
            >
              <div className="cost-templates-info">
                <p>Cost templates are predefined cost structures for routes and border crossings. Create and manage them in the Cost Templates page.</p>
                <div className="template-actions">
                  <button className="btn-primary" onClick={goToCostTemplates}>
                    Manage Cost Templates
                  </button>
                </div>
              </div>
              
              {/* Display existing templates for reference */}
              {clearances.length > 0 && (
                <div className="templates-reference">
                  <h4>Available Templates</h4>
                  <div className="table-wrap">
                    <table className="simple-table">
                      <thead>
                        <tr>
                          <th>Template Name</th>
                          <th>Border/Location</th>
                          <th>Total ({defaultCurrency})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clearances.slice(0, 5).map((c) => (
                          <tr key={c.id}>
                            <td>{c.name || "-"}</td>
                            <td>{c.border || "-"}</td>
                            <td>{(c.totalCostDefault || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {clearances.length > 5 && (
                      <div className="view-all-link">
                        <button className="btn-link" onClick={goToCostTemplates}>
                          View all {clearances.length} templates →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CollapsibleSection>

            {/* Trip Financials Section */}
            <CollapsibleSection
              title="Trip Financials"
              id="tripFinancials"
              isExpanded={expandedSections.tripFinancials}
              onToggle={handleSectionToggle}
              externalControl={true}
              className="trip-financials"
            >
              <form onSubmit={saveFinance} className="form-block">
                <div className="inputs-row">
                  <select
                    value={financeForm.tripCode}
                    onChange={(e) => handleTripChange(e.target.value)}
                  >
                    <option value="">Select Trip Code</option>
                    {trips.map((t) => (
                      <option key={t.id} value={t.trip_code}>
                        {t.trip_code}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filterTruck}
                    onChange={(e) => setFilterTruck(e.target.value)}
                  >
                    <option value="All">All Trucks</option>
                    {trucksList.map((truck) => (
                      <option key={truck} value={truck}>
                        {truck}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Enter Income"
                    value={financeForm.income}
                    onChange={(e) =>
                      setFinanceForm({ ...financeForm, income: e.target.value })
                    }
                    required
                  />
                  <select
                    className="currency-dropdown"
                    value={financeForm.currency}
                    onChange={(e) =>
                      setFinanceForm({ ...financeForm, currency: e.target.value })
                    }
                  >
                    <option value="">Select Currency</option>
                    {currenciesData.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={addExtraCostRow}
                  >
                    Add Extra Cost
                  </button>
                </div>
                <div className="buttons-row">
                  <button className="btn-primary" type="submit">
                    Save Financial Record
                  </button>
                </div>
                {financeForm.extraCosts?.map((cost, i) => (
                  <div key={i} className="inputs-row">
                    <input
                      placeholder="Description"
                      value={cost.desc}
                      onChange={(e) => handleExtraChange(i, "desc", e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={cost.amount}
                      onChange={(e) => handleExtraChange(i, "amount", e.target.value)}
                    />
                    <select
                      className="currency-dropdown"
                      value={cost.currency}
                      onChange={(e) => handleExtraChange(i, "currency", e.target.value)}
                    >
                      <option value="">Select Currency</option>
                      {currenciesData.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn-delete"
                      onClick={() =>
                        setFinanceForm({
                          ...financeForm,
                          extraCosts: financeForm.extraCosts.filter(
                            (_, index) => index !== i
                          ),
                        })
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </form>
              {filteredFinances.length === 0 ? (
                <p>No financial records found.</p>
              ) : (
                <div className="table-wrap">
                  <table className="simple-table">
                    <thead>
                      <tr>
                        <th>Trip Code</th>
                        <th>Truck ID</th>
                        <th>Distance (km)</th>
                        <th>Income ({defaultCurrency})</th>
                        <th>Cost ({defaultCurrency})</th>
                        <th>Profit ({defaultCurrency})</th>
                        <th>Extra Costs</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFinances.map((f) => {
                        const trip = trips.find((t) => t.trip_code === f.tripCode);
                        const route = trip
                          ? routes.find((r) => r.id === trip.route_id)
                          : null;
                        return (
                          <tr key={f.id}>
                            <td data-label="Trip Code">{f.tripCode || "-"}</td>
                            <td data-label="Truck ID">{f.truck_id || "-"}</td>
                            <td data-label="Distance">{route ? (route.total_distance_km || "-") : "-"}</td>
                            <td data-label="Income">{(f.incomeDefault || 0).toFixed(2)} (~{f.income || 0} {f.currency || "-"})</td>
                            <td data-label="Cost">{(f.totalCostDefault || 0).toFixed(2)}</td>
                            <td data-label="Profit">{(f.profit || 0).toFixed(2)}</td>
                            <td data-label="Extra Costs">
                              {f.extraCosts?.length > 0
                                ? f.extraCosts
                                    .map(
                                      (c) =>
                                        `${c.desc || "-"}: ${defaultCurrency} ${(c.amountDefault || 0).toFixed(2)} (~${c.amount || 0} ${c.currency || "-"})`
                                    )
                                    .join(", ")
                                : "-"}
                            </td>
                            <td className="actions-cell">
                              <button
                                className="menu-button"
                                onClick={() => setExpandedFinanceId(expandedFinanceId === f.id ? null : f.id)}
                              >
                                ⋮
                              </button>
                              <div className="dropdown" style={{ display: expandedFinanceId === f.id ? 'block' : 'none' }}>
                                <div
                                  className="dropdown-option"
                                  onClick={() => deleteFinance(f.id)}
                                >
                                  Delete
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>

            {/* Profit & Loss Summary Section */}
            <CollapsibleSection
              title="Profit & Loss Summary"
              id="profitLossSummary"
              isExpanded={expandedSections.profitLossSummary}
              onToggle={handleSectionToggle}
              externalControl={true}
            >
              {summary.length === 0 ? (
                <p>No profit and loss data available.</p>
              ) : (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={summary}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="income"
                        stroke="#8884d8"
                        name="Income"
                      />
                      <Line
                        type="monotone"
                        dataKey="cost"
                        stroke="#82ca9d"
                        name="Cost"
                      />
                      <Line
                        type="monotone"
                        dataKey="profit"
                        stroke="#ffc107"
                        name="Profit"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="export-buttons">
                <button className="btn-secondary" onClick={exportCSV}>
                  Export CSV
                </button>
                <button className="btn-secondary" onClick={exportPDF}>
                  Export PDF
                </button>
              </div>
            </CollapsibleSection>

            {/* Disposal History Section */}
            <CollapsibleSection
              title="Disposal History"
              id="disposalHistory"
              isExpanded={expandedSections.disposalHistory}
              onToggle={handleSectionToggle}
              externalControl={true}
            >
              {disposals.length === 0 ? (
                <p>No disposal records found.</p>
              ) : (
                <div className="table-wrap">
                  <table className="simple-table">
                    <thead>
                      <tr>
                        <th>Reg No</th>
                        <th>Type</th>
                        <th>Cost/Amount ({defaultCurrency})</th>
                        <th>Reason/Condition</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {disposals.map((d) => (
                        <tr key={d.id}>
                          <td data-label="Reg No">{d.reg_no || "-"}</td>
                          <td data-label="Type">{d.type === "write-off" ? "Write-Off" : "Sold"}</td>
                          <td data-label="Amount">
                            {(d.type === "write-off" ? d.cost || 0 : d.amount || 0).toFixed(2)}
                          </td>
                          <td data-label="Reason">{d.reason || d.condition || "-"}</td>
                          <td data-label="Date">
                            {d.date?.seconds
                              ? new Date(d.date.seconds * 1000).toLocaleDateString()
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CollapsibleSection>
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}